import { Controller, useFormContext } from 'react-hook-form'

import { Switch } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import withCustomizationFields, {
  WithCustomizationFieldsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import withFieldPermissions, {
  WithFieldPermissionsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { isReimbursableFieldEnabled } from 'modules/expenses/hooks/useExpensesCustomization'

const FIELD_NAME = 'payment'

interface Props extends WithCustomizationFieldsProps, WithFieldPermissionsProps {
  isAutomaticExpensable?: boolean
}

const PaymentSwitch = ({
  customizationFields,
  isAutomaticExpensable = false,
  currentEmployee,
  isFieldReadOnly,
}: Props) => {
  const { control, getValues, setValue, trigger } = useFormContext()
  const groupId = getValues('group')?.id
  const defaultReimbursableCurrency = currentEmployee?.legalEntity?.currency

  const handlePaymentUpdate = (payment: string): void => {
    setValue('paymentMethod', null, { shouldDirty: true })

    if (payment === 'not_reimbursable') {
      setValue('reimbursableAmount', null, { shouldDirty: true })
      setValue('reimbursableCurrency', null, { shouldDirty: true })
      trigger(['reimbursableAmount', 'reimbursableCurrency', 'paymentMethod'])
      return
    }

    const reimbursableAmountEnabled = isReimbursableFieldEnabled(
      customizationFields,
      payment,
      'reimbursable_amount'
    )
    const reimbursableCurrencyFieldEnabled = isReimbursableFieldEnabled(
      customizationFields,
      payment,
      'reimbursable_currency'
    )

    if (reimbursableAmountEnabled) {
      setValue('reimbursableAmount', getValues('amount'), {
        shouldDirty: true,
      })
    }

    if (reimbursableCurrencyFieldEnabled) {
      const currencyToSet = groupId ? defaultReimbursableCurrency : getValues('currency')
      setValue('reimbursableCurrency', currencyToSet, { shouldDirty: true })
    }

    trigger(['reimbursableAmount', 'reimbursableCurrency', 'paymentMethod'])
  }

  return (
    <Controller
      name={FIELD_NAME}
      control={control}
      render={({ field }) => (
        <Switch
          {...field}
          checked={field.value === 'reimbursable'}
          data-test="reimbursable-switch"
          onCheckedChange={(value) => {
            const returnValue = value ? 'reimbursable' : 'not_reimbursable'
            field.onChange(returnValue)
            handlePaymentUpdate(returnValue)
          }}
          title={i18n.t('expenses_v2.forms.fields.payment.new_label')}
          disabled={isFieldReadOnly || isAutomaticExpensable}
        />
      )}
    />
  )
}

export default withCustomizationFields(withFieldPermissions(PaymentSwitch, FIELD_NAME))
